import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Sparkle, Robot } from "@phosphor-icons/react";

export default function AIGenerateModal({ open, onOpenChange, onCreated, defaultSubjectId = "" }) {
  const [form, setForm] = useState({
    topic: "",
    count: 5,
    difficulty: "medium",
    type: "mcq",
    provider: "claude",
    subject_id: defaultSubjectId,
    save: true,
  });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const generate = async () => {
    if (!form.topic.trim()) { toast.error("Topic required"); return; }
    setBusy(true);
    setPreview(null);
    try {
      const res = await api.post("/ai/generate-questions", { ...form, count: Number(form.count) });
      setPreview(res.data.questions);
      toast.success(`Generated ${res.data.count} questions`);
      if (form.save && onCreated) onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI generation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ai-generate-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkle size={22} weight="fill" className="text-zinc-900" />
            AI Question Generator
          </DialogTitle>
          <DialogDescription>
            Generate exam-ready questions from any topic using Claude Sonnet 4.5 or GPT-5.2.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <Field label="Topic">
            <input
              value={form.topic}
              onChange={(e) => setForm({ ...form, topic: e.target.value })}
              placeholder="e.g. Newton's laws, SQL joins, World War 2"
              className="w-full border border-zinc-300 px-3 py-2.5 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="ai-topic-input"
            />
          </Field>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Count">
              <input
                type="number"
                min={1}
                max={20}
                value={form.count}
                onChange={(e) => setForm({ ...form, count: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
                data-testid="ai-count-input"
              />
            </Field>
            <Field label="Difficulty">
              <select
                value={form.difficulty}
                onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white"
                data-testid="ai-difficulty"
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </Field>
            <Field label="Type">
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white"
                data-testid="ai-type"
              >
                <option value="mcq">MCQ</option>
                <option value="true_false">True/False</option>
                <option value="short_answer">Short Answer</option>
              </select>
            </Field>
            <Field label="Provider">
              <select
                value={form.provider}
                onChange={(e) => setForm({ ...form, provider: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white"
                data-testid="ai-provider"
              >
                <option value="claude">Claude 4.5</option>
                <option value="openai">GPT-5.2</option>
              </select>
            </Field>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.save}
              onChange={(e) => setForm({ ...form, save: e.target.checked })}
              data-testid="ai-save-checkbox"
            />
            <span className="text-sm">Save generated questions to bank</span>
          </label>

          <button
            onClick={generate}
            disabled={busy}
            className="w-full bg-zinc-900 text-white py-3 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="ai-generate-btn"
          >
            <Robot size={16} />
            {busy ? "Generating…" : `Generate ${form.count} ${form.type.toUpperCase()} questions`}
          </button>

          {preview && (
            <div className="border border-zinc-200" data-testid="ai-preview">
              <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
                Preview · {preview.length} questions
              </div>
              {preview.map((q, i) => (
                <div key={i} className="p-4 border-b border-zinc-200 last:border-b-0">
                  <div className="text-sm font-medium">{i + 1}. {q.text}</div>
                  {q.options?.length > 0 && (
                    <div className="mt-2 text-xs text-zinc-600 space-y-0.5">
                      {q.options.map((o, idx) => (
                        <div key={idx} className={q.correct_answers?.includes(String(idx)) ? "text-green-700 font-medium" : ""}>
                          {String.fromCharCode(65 + idx)}. {o}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">{label}</div>
      {children}
    </div>
  );
}
